import { useSessionStore } from "../../../state/src/useSessionStore";
import type { ConsumerLookupInfo } from "../view-models/ConsumerLookupInfo";
import type { SenderLookupInfo } from "../view-models/SenderLookupInfo";

/**
 * ConsumerLookup may return SenderLookupInfos (profile from another system, no local PIN)
 * instead of ConsumerInfos (kiosk profile). In that case we treat the user like a new
 * customer on this app and pre-fill sender fields from the first SenderLookupInfo.
 */
export function isSenderLookupOnlyConsumerResponse(info: unknown): boolean {
	const data = info as ConsumerLookupInfo | undefined;
	const sl = data?.SenderLookupInfos;
	const ci = data?.ConsumerInfos;
	if (!Array.isArray(sl) || sl.length === 0) return false;
	if (Array.isArray(ci) && ci.length > 0) return false;
	return true;
}

function digitsLast10(phone: string | undefined | null): string | null {
	if (phone == null || phone === "") return null;
	const d = String(phone).replace(/\D/g, "");
	if (d.length >= 10) return d.slice(-10);
	return d.length > 0 ? d : null;
}

function trimOrNull(v: unknown): string | null {
	if (v === undefined || v === null) return null;
	const t = String(v).trim();
	return t.length ? t : null;
}

export function applyFirstSenderLookupInfoToSession(lookup: ConsumerLookupInfo): void {
	const raw = lookup.SenderLookupInfos?.[0] as SenderLookupInfo | undefined;
	if (!raw) return;
	const s = useSessionStore.getState();

	s.setSenderFirstName?.(trimOrNull(raw.SenderFirstName));
	s.setSenderMiddleName?.(trimOrNull(raw.SenderMiddleName));
	s.setSenderLastName?.(trimOrNull(raw.SenderLastName));
	s.setSenderLastName2?.(trimOrNull(raw.SenderLastName2));

	const addrParts = [raw.SenderAddress, raw.SenderAddress2, raw.SenderAddress3]
		.map((x) => (x == null ? "" : String(x).trim()))
		.filter(Boolean);
	if (addrParts.length) s.setSenderAddress?.(addrParts.join(", "));

	s.setSenderCity?.(trimOrNull(raw.SenderCity));
	s.setSenderState?.(trimOrNull(raw.SenderState));
	s.setSenderZipCode?.(trimOrNull(raw.SenderZipCode));

	const ph = digitsLast10(raw.SenderHomePhone);
	if (ph) s.setPhoneNumber?.(ph);

	s.setConsumerPin?.(null);
	s.setConsumerId?.(null);
}
